// OIDC Authorization Code + PKCE, hand-rolled on Web Crypto so we can stay
// inside the dep budget (react / react-dom / react-router / mui only).
//
// Used by every non-embedded deploy target. Embedded mode bypasses this
// entirely and trusts the NetSuite Suitelet session cookie.
//
// Token storage: sessionStorage (cleared when the tab closes). The access
// token is read by adapter.rest.js as the Bearer for every API call.

import { config } from "../config";

const STORAGE_KEY = "mr.b2b.oidc";
const PKCE_KEY = "mr.b2b.oidc.pkce";

let discoveryPromise = null;

function b64url(bytes) {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function randomVerifier() {
  const bytes = new Uint8Array(64);
  crypto.getRandomValues(bytes);
  return b64url(bytes);
}

async function challenge(verifier) {
  const buf = new TextEncoder().encode(verifier);
  const hash = await crypto.subtle.digest("SHA-256", buf);
  return b64url(new Uint8Array(hash));
}

function discovery() {
  if (discoveryPromise) return discoveryPromise;
  if (!config.oidc.authority) {
    return Promise.reject(new Error("VITE_OIDC_AUTHORITY is not set"));
  }
  const url = `${config.oidc.authority.replace(/\/$/, "")}/.well-known/openid-configuration`;
  discoveryPromise = fetch(url).then((r) => {
    if (!r.ok) throw new Error(`OIDC discovery failed: ${r.status}`);
    return r.json();
  });
  return discoveryPromise;
}

export function getStoredTokens() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch { return null; }
}

function setStoredTokens(t) {
  if (t) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(t));
  else sessionStorage.removeItem(STORAGE_KEY);
}

export function getAccessToken() {
  const t = getStoredTokens();
  if (!t) return null;
  if (t.expiresAt && Date.now() > t.expiresAt) return null;
  return t.accessToken || null;
}

export function getIdTokenClaims() {
  const t = getStoredTokens();
  if (!t?.idToken) return null;
  try {
    const [, payload] = t.idToken.split(".");
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decodeURIComponent(escape(json)));
  } catch { return null; }
}

export async function beginLogin(returnTo) {
  const meta = await discovery();
  const verifier = randomVerifier();
  const code_challenge = await challenge(verifier);
  const state = randomVerifier().slice(0, 32);
  const nonce = randomVerifier().slice(0, 32);
  sessionStorage.setItem(PKCE_KEY, JSON.stringify({
    verifier, state, nonce, returnTo: returnTo || "/",
  }));
  const url = new URL(meta.authorization_endpoint);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("client_id", config.oidc.clientId);
  url.searchParams.set("redirect_uri", config.oidc.redirectUri);
  url.searchParams.set("scope", config.oidc.scope);
  url.searchParams.set("code_challenge", code_challenge);
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("state", state);
  url.searchParams.set("nonce", nonce);
  window.location.assign(url.toString());
}

export async function completeLogin() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (!code) throw new Error("OIDC callback missing code");
  const pkceRaw = sessionStorage.getItem(PKCE_KEY);
  if (!pkceRaw) throw new Error("OIDC PKCE state missing");
  const pkce = JSON.parse(pkceRaw);
  if (pkce.state !== state) throw new Error("OIDC state mismatch");
  sessionStorage.removeItem(PKCE_KEY);

  const meta = await discovery();
  const body = new URLSearchParams({
    grant_type: "authorization_code",
    code,
    redirect_uri: config.oidc.redirectUri,
    client_id: config.oidc.clientId,
    code_verifier: pkce.verifier,
  });
  const res = await fetch(meta.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`OIDC token exchange failed: ${res.status} ${text}`);
  }
  const tok = await res.json();
  const expiresAt = tok.expires_in ? Date.now() + (tok.expires_in - 30) * 1000 : null;
  setStoredTokens({
    accessToken: tok.access_token,
    idToken: tok.id_token,
    refreshToken: tok.refresh_token,
    tokenType: tok.token_type || "Bearer",
    expiresAt,
  });
  return { returnTo: pkce.returnTo || "/" };
}

export async function refresh() {
  const t = getStoredTokens();
  if (!t?.refreshToken) return null;
  const meta = await discovery();
  const body = new URLSearchParams({
    grant_type: "refresh_token",
    refresh_token: t.refreshToken,
    client_id: config.oidc.clientId,
  });
  const res = await fetch(meta.token_endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!res.ok) { setStoredTokens(null); return null; }
  const tok = await res.json();
  const expiresAt = tok.expires_in ? Date.now() + (tok.expires_in - 30) * 1000 : null;
  const next = {
    accessToken: tok.access_token,
    idToken: tok.id_token || t.idToken,
    refreshToken: tok.refresh_token || t.refreshToken,
    tokenType: tok.token_type || "Bearer",
    expiresAt,
  };
  setStoredTokens(next);
  return next;
}

export async function logout() {
  const t = getStoredTokens();
  setStoredTokens(null);
  try {
    const meta = await discovery();
    if (meta.end_session_endpoint) {
      const url = new URL(meta.end_session_endpoint);
      if (t?.idToken) url.searchParams.set("id_token_hint", t.idToken);
      url.searchParams.set("post_logout_redirect_uri", config.oidc.postLogoutRedirectUri);
      window.location.assign(url.toString());
      return;
    }
  } catch { /* fall through */ }
  window.location.assign(config.oidc.postLogoutRedirectUri || "/");
}
